import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api, newIdempotencyKey } from '../api/client'
import type { Claim, Shipment } from '../api/types'
import {
  Empty,
  ErrorBanner,
  Loading,
  Panel,
  StatusChip,
  dateTime,
  inr,
} from '../components/ui'

/** The reasons the API accepts, in the words a customer would use. */
const CLAIM_TYPES: { value: string; label: string; hint: string }[] = [
  { value: 'shortage', label: 'Shortage', hint: 'Fewer packages arrived than were sent' },
  { value: 'damage', label: 'Damage', hint: 'Goods arrived damaged' },
  { value: 'loss', label: 'Loss', hint: 'The consignment never arrived' },
  { value: 'delay', label: 'Delay', hint: 'It arrived late and the delay cost you' },
  { value: 'wrong_delivery', label: 'Wrong delivery', hint: 'Delivered to the wrong party' },
  { value: 'missing_document', label: 'Missing document', hint: 'Paperwork did not arrive with the goods' },
]

/** How far along a claim is, so "raised" does not read as "ignored". */
const CLAIM_STAGE: Record<string, string> = {
  raised: 'With the transporter, awaiting acknowledgement',
  acknowledged: 'Acknowledged — being investigated',
  investigating: 'Under investigation',
  approved: 'Approved — settlement being processed',
  settled: 'Settled',
  rejected: 'Declined',
  closed: 'Closed',
}

function RaiseClaimForm({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient()

  const [form, setForm] = useState({
    claim_type: 'damage',
    shipment_id: '',
    claimed_amount: '',
    description: '',
  })

  // Claims are almost always about a specific consignment, and picking one from
  // a list beats typing an id the customer would have to go and look up.
  const shipmentsQuery = useQuery({
    queryKey: ['portal-shipments'],
    queryFn: () => api.get<Shipment[]>('/portal/shipments').then((r) => r.data),
  })

  const raise = useMutation({
    mutationFn: () =>
      api.post<Claim>(
        '/portal/claims',
        {
          claim_type: form.claim_type,
          description: form.description,
          ...(form.claimed_amount ? { claimed_amount: Number(form.claimed_amount) } : {}),
          ...(form.shipment_id ? { shipment_id: Number(form.shipment_id) } : {}),
        },
        // A double-tap on a slow connection must not raise the claim twice.
        newIdempotencyKey(),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['portal-claims'] })
      void queryClient.invalidateQueries({ queryKey: ['portal-dashboard'] })
      onDone()
    },
  })

  const fieldErrors = raise.error instanceof ApiError ? raise.error.fieldErrors() : {}
  const shipments = shipmentsQuery.data ?? []
  const chosen = CLAIM_TYPES.find((t) => t.value === form.claim_type)

  return (
    <Panel title="Raise a claim">
      <div className="stack" style={{ gap: 14 }}>
        {raise.error && <ErrorBanner error={raise.error} />}

        <div className="field">
          <label className="label" htmlFor="claim_type">
            What went wrong
          </label>
          <select
            id="claim_type"
            className="input"
            value={form.claim_type}
            onChange={(e) => setForm((f) => ({ ...f, claim_type: e.target.value }))}
          >
            {CLAIM_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          {chosen && <span className="page-sub">{chosen.hint}</span>}
          {fieldErrors.claim_type && <span className="field-error">{fieldErrors.claim_type}</span>}
        </div>

        <div className="field">
          <label className="label" htmlFor="shipment_id">
            Which consignment
          </label>
          <select
            id="shipment_id"
            className="input"
            value={form.shipment_id}
            onChange={(e) => setForm((f) => ({ ...f, shipment_id: e.target.value }))}
          >
            <option value="">Not about one consignment in particular</option>
            {shipments.map((shipment) => (
              <option key={shipment.id} value={shipment.id}>
                {shipment.shipment_number} · {shipment.origin_name} → {shipment.destination_name}
              </option>
            ))}
          </select>
          {fieldErrors.shipment_id && <span className="field-error">{fieldErrors.shipment_id}</span>}
        </div>

        <div className="field">
          <label className="label" htmlFor="claimed_amount">
            Amount claimed (₹)
          </label>
          <input
            id="claimed_amount"
            className="input"
            inputMode="decimal"
            placeholder="Leave blank if you do not have a figure yet"
            value={form.claimed_amount}
            onChange={(e) => setForm((f) => ({ ...f, claimed_amount: e.target.value }))}
          />
          {fieldErrors.claimed_amount && (
            <span className="field-error">{fieldErrors.claimed_amount}</span>
          )}
        </div>

        <div className="field">
          <label className="label" htmlFor="description">
            What happened
          </label>
          <textarea
            id="description"
            className="input"
            value={form.description}
            placeholder="Describe the damage, the shortage or the delay, and anything the transporter will need to check it."
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
          {fieldErrors.description && <span className="field-error">{fieldErrors.description}</span>}
        </div>

        <div className="row">
          <button
            className="btn btn-primary"
            disabled={raise.isPending || form.description.trim().length === 0}
            onClick={() => raise.mutate()}
          >
            {raise.isPending ? 'Sending…' : 'Raise this claim'}
          </button>
          <button className="btn" onClick={onDone} disabled={raise.isPending}>
            Cancel
          </button>
        </div>
      </div>
    </Panel>
  )
}

export default function ClaimsPage() {
  const [raising, setRaising] = useState(false)

  const claimsQuery = useQuery({
    queryKey: ['portal-claims'],
    queryFn: () => api.get<Claim[]>('/portal/claims').then((r) => r.data),
  })

  const claims = claimsQuery.data ?? []
  const open = claims.filter((c) => !['settled', 'rejected', 'closed'].includes(c.status))

  return (
    <div className="stack">
      <div className="page-head">
        <div className="page-title">Claims</div>
        <div className="page-sub">
          Shortage, damage, loss or delay — raised here, and answered here
        </div>
      </div>

      {!raising && (
        <div className="row">
          <button className="btn btn-primary" onClick={() => setRaising(true)}>
            Raise a claim
          </button>
          {open.length > 0 && (
            <span className="page-sub">
              {open.length} claim(s) still open with the transporter
            </span>
          )}
        </div>
      )}

      {raising && <RaiseClaimForm onDone={() => setRaising(false)} />}

      <Panel title={`${claims.length} claim(s)`} bodyless>
        {claimsQuery.isLoading ? (
          <Loading />
        ) : claimsQuery.error ? (
          <div style={{ padding: 16 }}>
            <ErrorBanner error={claimsQuery.error} />
          </div>
        ) : claims.length === 0 ? (
          <Empty
            title="No claims raised"
            hint="If a consignment arrives short, damaged or late, raise it here and the transporter will respond."
          />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Claim</th>
                <th>About</th>
                <th>Reason</th>
                <th>Where it stands</th>
                <th className="num">Claimed</th>
                <th className="num">Settled</th>
                <th>Raised</th>
              </tr>
            </thead>
            <tbody>
              {claims.map((claim) => (
                <tr key={claim.id}>
                  <td data-label="Claim" className="mono">
                    {claim.claim_number}
                  </td>
                  <td data-label="About" className="mono">
                    {claim.shipment_number ?? '—'}
                  </td>
                  <td data-label="Reason">{claim.claim_type.replace(/_/g, ' ')}</td>
                  <td data-label="Where it stands">
                    <StatusChip status={claim.status} />
                    <div className="page-sub">{CLAIM_STAGE[claim.status] ?? ''}</div>
                    {claim.resolution_notes && (
                      <div className="page-sub">{claim.resolution_notes}</div>
                    )}
                  </td>
                  <td data-label="Claimed" className="num">
                    {inr(claim.claimed_amount)}
                  </td>
                  <td data-label="Settled" className="num">
                    {Number(claim.settled_amount ?? 0) > 0 ? (
                      <>
                        {inr(claim.settled_amount)}
                        {claim.credit_note_ref && (
                          <div className="page-sub mono">{claim.credit_note_ref}</div>
                        )}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td data-label="Raised">{dateTime(claim.raised_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  )
}
